// Flash Card Storage Service
// Handles local storage for flash cards with future Supabase backend integration

class FlashCardStorageService {
  constructor() {
    this.storageKey = 'essayright_saved_flashcards';
    this.setsStorageKey = 'essayright_flashcard_sets';
  }

  // Get all saved flash cards
  getSavedCards() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading saved flash cards:', error);
      return [];
    }
  }

  // Save a flash card
  saveCard(card, setInfo = null) {
    try {
      const saved = this.getSavedCards();
      const cardWithMetadata = {
        ...card,
        savedAt: new Date().toISOString(),
        setInfo: setInfo ? {
          title: setInfo.title,
          language: setInfo.language,
          difficulty: setInfo.difficulty,
          category: setInfo.category
        } : null,
        id: card.id || this.generateCardId(card)
      };

      // Check if card already exists
      const existingIndex = saved.findIndex(c => c.id === cardWithMetadata.id);
      
      if (existingIndex >= 0) {
        saved[existingIndex] = cardWithMetadata;
      } else {
        saved.push(cardWithMetadata);
      }

      localStorage.setItem(this.storageKey, JSON.stringify(saved));
      return true;
    } catch (error) {
      console.error('Error saving flash card:', error);
      return false;
    }
  }

  // Remove a saved flash card
  removeSavedCard(cardId) {
    try {
      const saved = this.getSavedCards();
      const filtered = saved.filter(card => card.id !== cardId);
      localStorage.setItem(this.storageKey, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Error removing flash card:', error);
      return false;
    }
  }

  // Check if a card is saved
  isCardSaved(cardId) {
    const saved = this.getSavedCards();
    return saved.some(card => card.id === cardId);
  }

  // Save an entire flash card set
  saveFlashCardSet(flashCardSet) {
    try {
      const sets = this.getSavedSets();
      const setWithMetadata = {
        ...flashCardSet,
        savedAt: new Date().toISOString(),
        id: flashCardSet.id || this.generateSetId(flashCardSet)
      };

      // Check if set already exists
      const existingIndex = sets.findIndex(s => s.id === setWithMetadata.id);
      
      if (existingIndex >= 0) {
        sets[existingIndex] = setWithMetadata;
      } else {
        sets.push(setWithMetadata);
      }

      localStorage.setItem(this.setsStorageKey, JSON.stringify(sets));
      return setWithMetadata.id;
    } catch (error) {
      console.error('Error saving flash card set:', error);
      return null;
    }
  }

  // Get all saved flash card sets
  getSavedSets() {
    try {
      const saved = localStorage.getItem(this.setsStorageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading saved flash card sets:', error);
      return [];
    }
  }

  // Remove a saved flash card set
  removeSavedSet(setId) {
    try {
      const sets = this.getSavedSets();
      const filtered = sets.filter(set => set.id !== setId);
      localStorage.setItem(this.setsStorageKey, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Error removing flash card set:', error);
      return false;
    }
  }

  // Get saved cards grouped by set
  getSavedCardsBySet() {
    const cards = this.getSavedCards();
    const grouped = {};

    cards.forEach(card => {
      const setKey = card.setInfo ? card.setInfo.title : 'Individual Cards';
      if (!grouped[setKey]) {
        grouped[setKey] = {
          title: setKey,
          cards: [],
          setInfo: card.setInfo
        };
      }
      grouped[setKey].cards.push(card);
    });

    return grouped;
  }

  // Export saved data (for future backend sync)
  exportSavedData() {
    return {
      cards: this.getSavedCards(),
      sets: this.getSavedSets(),
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
  }

  // Import saved data (for future backend sync)
  importSavedData(data) {
    try {
      if (data.cards) {
        localStorage.setItem(this.storageKey, JSON.stringify(data.cards));
      }
      if (data.sets) {
        localStorage.setItem(this.setsStorageKey, JSON.stringify(data.sets));
      }
      return true;
    } catch (error) {
      console.error('Error importing flash card data:', error);
      return false;
    }
  }

  // Clear all saved data
  clearAllSavedData() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.setsStorageKey);
      return true;
    } catch (error) {
      console.error('Error clearing saved data:', error);
      return false;
    }
  }

  // Generate unique ID for card
  generateCardId(card) {
    const content = (card.question + card.answer).replace(/[^a-zA-Z0-9]/g, '');
    const timestamp = Date.now();
    return `card_${content.substring(0, 10)}_${timestamp}`;
  }

  // Generate unique ID for set
  generateSetId(set) {
    const title = set.title.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const timestamp = Date.now();
    return `set_${title}_${timestamp}`;
  }

  // Get storage statistics
  getStorageStats() {
    const cards = this.getSavedCards();
    const sets = this.getSavedSets();
    
    return {
      totalCards: cards.length,
      totalSets: sets.length,
      languages: [...new Set(cards.map(c => c.setInfo?.language).filter(Boolean))],
      categories: [...new Set(cards.map(c => c.setInfo?.category).filter(Boolean))],
      lastSaved: cards.length > 0 ? Math.max(...cards.map(c => new Date(c.savedAt).getTime())) : null
    };
  }

  // Future Supabase integration methods (placeholder)
  async syncWithSupabase() {
    // TODO: Implement Supabase sync
    console.log('Supabase sync not yet implemented');
    return false;
  }

  async uploadToSupabase(data) {
    // TODO: Implement Supabase upload
    console.log('Supabase upload not yet implemented');
    return false;
  }

  async downloadFromSupabase() {
    // TODO: Implement Supabase download
    console.log('Supabase download not yet implemented');
    return null;
  }
}

const flashCardStorageService = new FlashCardStorageService();
export default flashCardStorageService; 